import React, { useEffect, useMemo, useState } from 'react';
import api from '../lib/api';
import { ResponsiveDialog, ResponsiveDialogHeader, ResponsiveDialogBody, ResponsiveDialogFooter } from './ResponsiveDialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { LineItemsEditor } from './LineItemsEditor';
import { TaxSection } from './TaxSection';
import { ClientPicker } from './ClientPicker';
import { computeInvoiceTotals } from '../lib/invoiceTax';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { toInputDate, fromInputDate } from '../lib/format';

const blankTax = {
  include_ppn: false,
  ppn_rate: null,
  include_withholding_tax: false,
  withholding_tax_type: 'PPh',
  withholding_tax_rate: null,
  withholding_tax_base: 'dpp',
  withholding_tax_custom_base: 0,
  withholding_status: 'Waiting',
  withholding_certificate_number: '',
  withholding_certificate_date: '',
};

const blank = {
  client_id: '',
  invoice_date: '',
  event_date: '',
  due_date: '',
  venue: '',
  event_description: '',
  status: 'Draft',
  notes: '',
  payment_instructions: '',
  items: [],
  final_discount_type: 'nominal',
  final_discount_value: '0',
  tax: { ...blankTax },
};

export const InvoiceFormDialog = ({ open, onOpenChange, editing, onSaved, defaultClientId }) => {
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState(blank);
  const [settings, setSettings] = useState({});
  const [saving, setSaving] = useState(false);

  const refreshClients = async () => {
    const { data } = await api.get('/clients');
    setClients(data);
  };

  useEffect(() => {
    if (open) {
      api.get('/clients').then(r => setClients(r.data));
      api.get('/settings').then(r => {
        setSettings(r.data || {});
        if (!editing) {
          setForm(f => ({
            ...f,
            payment_instructions: r.data?.default_payment_instructions || '',
            tax: {
              ...f.tax,
              ppn_rate: r.data?.default_ppn_rate ?? null,
              withholding_tax_rate: r.data?.default_withholding_tax_rate ?? null,
              withholding_tax_type: r.data?.default_withholding_tax_type || 'PPh',
              withholding_tax_base: r.data?.default_withholding_tax_base || 'dpp',
            },
          }));
        }
      });
    }
    // eslint-disable-next-line
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        client_id: editing.client_id || '',
        invoice_date: toInputDate(editing.invoice_date),
        event_date: toInputDate(editing.event_date),
        due_date: toInputDate(editing.due_date),
        venue: editing.venue || '',
        event_description: editing.event_description || '',
        status: editing.status || 'Draft',
        notes: editing.notes || '',
        payment_instructions: editing.payment_instructions || '',
        items: (editing.items || []).map(it => ({ ...it })),
        final_discount_type: editing.final_discount_type || 'nominal',
        final_discount_value: editing.final_discount_value != null ? String(editing.final_discount_value) : '0',
        tax: {
          include_ppn: !!editing.include_ppn,
          ppn_rate: editing.ppn_rate ?? null,
          include_withholding_tax: !!editing.include_withholding_tax,
          withholding_tax_type: editing.withholding_tax_type || 'PPh',
          withholding_tax_rate: editing.withholding_tax_rate ?? null,
          withholding_tax_base: editing.withholding_tax_base || 'dpp',
          withholding_tax_custom_base: editing.withholding_tax_custom_base || 0,
          withholding_status: editing.withholding_status || 'Waiting',
          withholding_certificate_number: editing.withholding_certificate_number || '',
          withholding_certificate_date: toInputDate(editing.withholding_certificate_date) || '',
        },
      });
    } else {
      setForm({ ...blank, invoice_date: toInputDate(new Date()), client_id: defaultClientId || '' });
    }
  }, [open, editing, defaultClientId]);

  const totals = useMemo(() => computeInvoiceTotals(
    form.items,
    form.final_discount_type,
    form.final_discount_value,
    form.tax,
    settings,
  ), [form.items, form.final_discount_type, form.final_discount_value, form.tax, settings]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_id) { toast.error('Pilih client'); return; }
    if (form.items.length === 0) { toast.error('Minimal 1 item'); return; }
    setSaving(true);
    try {
      const t = form.tax || {};
      const payload = {
        client_id: form.client_id,
        invoice_date: fromInputDate(form.invoice_date),
        event_date: fromInputDate(form.event_date),
        due_date: fromInputDate(form.due_date),
        venue: form.venue,
        event_description: form.event_description,
        status: form.status,
        notes: form.notes,
        payment_instructions: form.payment_instructions,
        final_discount_type: (form.final_discount_type || 'nominal').toLowerCase(),
        final_discount_value: Number(form.final_discount_value) || 0,
        items: form.items.map(it => ({
          item_name: it.item_name,
          description: it.description || '',
          quantity: Number(it.quantity) || 0,
          unit: it.unit || '',
          unit_price: Number(it.unit_price) || 0,
          discount: Number(it.discount) || 0,
          product_id: it.product_id || null,
          package_id: it.package_id || null,
          package_group_name: it.package_group_name || null,
          item_type: it.item_type || 'custom',
        })),
        // tax fields (e-Faktur method: rate handled internally as 12)
        include_ppn: !!t.include_ppn,
        ppn_rate: t.include_ppn ? 12 : null,
        include_withholding_tax: !!t.include_withholding_tax,
        withholding_tax_type: t.withholding_tax_type || 'PPh',
        withholding_tax_rate: t.include_withholding_tax ? (Number(t.withholding_tax_rate) || 0) : null,
        withholding_tax_base: t.withholding_tax_base || 'dpp',
        withholding_tax_custom_base: Number(t.withholding_tax_custom_base) || 0,
        withholding_status: t.include_withholding_tax ? (t.withholding_status || 'Waiting') : null,
        withholding_certificate_number: t.withholding_certificate_number || '',
        withholding_certificate_date: fromInputDate(t.withholding_certificate_date),
      };
      if (editing) {
        await api.put(`/invoices/${editing.id}`, payload);
        toast.success('Invoice diperbarui');
      } else {
        await api.post('/invoices', payload);
        toast.success('Invoice dibuat');
      }
      onOpenChange(false);
      onSaved && onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Gagal menyimpan');
    } finally { setSaving(false); }
  };

  return (
    <ResponsiveDialog open={open} onOpenChange={onOpenChange} testid="invoice-form-dialog" size="xl">
      <form onSubmit={submit} className="flex flex-col min-h-0 flex-1 h-full">
        <ResponsiveDialogHeader
          title={editing ? `Edit Invoice ${editing.invoice_number}` : 'Invoice Baru'}
          description="Lengkapi detail invoice dan item."
        />
        <ResponsiveDialogBody>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Client *</Label>
              <ClientPicker
                value={form.client_id}
                onChange={(v) => setForm({ ...form, client_id: v })}
                clients={clients}
                onClientsRefresh={refreshClients}
                testid="invoice-form-client"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="h-11 md:h-9 text-base md:text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Draft">Draft</SelectItem>
                  <SelectItem value="Sent">Terkirim</SelectItem>
                  <SelectItem value="Cancelled">Dibatalkan</SelectItem>
                  <SelectItem value="Dispute">Dispute</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Tanggal Invoice</Label>
              <Input className="h-11 md:h-9 text-base md:text-sm date-input-mobile" type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Jatuh Tempo</Label>
              <Input className="h-11 md:h-9 text-base md:text-sm date-input-mobile" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Tanggal Event</Label>
              <Input className="h-11 md:h-9 text-base md:text-sm date-input-mobile" type="date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Venue</Label>
              <Input className="h-11 md:h-9 text-base md:text-sm" value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label className="text-xs">Deskripsi Event</Label>
              <Input className="h-11 md:h-9 text-base md:text-sm" value={form.event_description} onChange={(e) => setForm({ ...form, event_description: e.target.value })} />
            </div>
          </div>

          <LineItemsEditor
            items={form.items}
            onChange={(items) => setForm({ ...form, items })}
            testidPrefix="invoice-item"
            finalDiscount={{ type: form.final_discount_type, value: form.final_discount_value }}
            onFinalDiscountChange={(fd) => setForm({ ...form, final_discount_type: fd.type, final_discount_value: fd.value })}
          />

          <TaxSection
            tax={form.tax}
            onChange={(t) => setForm({ ...form, tax: t })}
            totals={totals}
            settings={settings}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Catatan</Label>
              <Textarea rows={3} className="text-base md:text-sm" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Instruksi Pembayaran</Label>
              <Textarea rows={3} className="text-base md:text-sm" value={form.payment_instructions} onChange={(e) => setForm({ ...form, payment_instructions: e.target.value })} />
            </div>
          </div>
        </ResponsiveDialogBody>
        <ResponsiveDialogFooter>
          <Button
            type="button"
            variant="ghost"
            className="h-11 md:h-9 w-full sm:w-auto"
            onClick={() => onOpenChange(false)}
          >
            Batal
          </Button>
          <Button
            type="submit"
            disabled={saving}
            className="h-11 md:h-9 w-full sm:w-auto"
            data-testid="invoice-form-save"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Simpan
          </Button>
        </ResponsiveDialogFooter>
      </form>
    </ResponsiveDialog>
  );
};
